/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IHeroBannerViewProps } from './hero-banner';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, Keyboard } from 'swiper/modules';

export default (props: IHeroBannerViewProps) => {
    const { config, slots } = props;
    const paginationRef = React.useRef<HTMLDivElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const slides = slots?.slides || [];
    const autoplayDelay = config.autoplayDelay ? config.autoplayDelay * 1000 : 8000;
    const enableLoop = config.enableLoop !== false;
    const pauseOnHover = config.pauseOnHover !== false;
    const limitMaxHeight = config.limitMaxHeight || false;

    const [heroBannerCustomClass, setHeroBannerCustomClass] = React.useState('');
    const [currentPaginationTheme, setCurrentPaginationTheme] = React.useState('light');

    React.useEffect(() => {
        if (containerRef.current) {
            containerRef.current.style.setProperty('--progress-duration', `${autoplayDelay}ms`);
        }
    }, [autoplayDelay]);

    if (!slides || slides.length === 0) {
        return (
            <div className='ms-hero-banner'>
                <div className='hero-banner-placeholder'>
                    <p>Please add slides to the hero banner in Site Builder</p>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className={`ms-hero-banner ${heroBannerCustomClass} pagination-theme-${currentPaginationTheme}`}
            data-limit-height={limitMaxHeight}
            onMouseEnter={() => {
                setHeroBannerCustomClass('swiper-paused');
            }}
            onMouseLeave={() => {
                setHeroBannerCustomClass('');
            }}
        >
            <Swiper
                spaceBetween={-1}
                slidesPerView={1}
                loop={enableLoop}
                autoplay={{
                    delay: autoplayDelay,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: pauseOnHover
                }}
                pagination={{
                    el: paginationRef.current,
                    clickable: true,
                    type: 'bullets',
                    bulletClass: 'swiper-pagination-bullet-custom',
                    bulletActiveClass: 'swiper-pagination-bullet-active-custom'
                }}
                keyboard={{ enabled: true, onlyInViewport: true }}
                modules={[Navigation, Pagination, Autoplay, Keyboard]}
                role='region'
                aria-label='banner'
                aria-roledescription='carousel'
                onSlideChange={(swiper: any) => {
                    const activeSlide = swiper.slides[swiper.activeIndex];
                    const innerSlide = activeSlide?.querySelector('[data-pagination-theme]');
                    const theme = innerSlide?.getAttribute('data-pagination-theme') || 'light';
                    setCurrentPaginationTheme(theme);
                }}
                onInit={(swiper: any) => {
                    const activeSlide = swiper.slides[swiper.activeIndex];
                    const innerSlide = activeSlide?.querySelector('[data-pagination-theme]');
                    const theme = innerSlide?.getAttribute('data-pagination-theme') || 'light';
                    setCurrentPaginationTheme(theme);
                }}
            >
                {slides.map((slide, index) => (
                    <SwiperSlide
                        key={index}
                        role='group'
                        aria-roledescription='slide'
                        aria-label={`Slide ${index + 1} of ${slides.length}`}
                    >
                        {slide}
                    </SwiperSlide>
                ))}
            </Swiper>
            {slides.length > 1 && (
                <div className='container'>
                    <div aria-label='slider' ref={paginationRef} className='swiper-pagination-custom'></div>
                </div>
            )}
        </div>
    );
};
